import { useInPersonEventInformation } from '@hooks/useInPersonEventInformation';
import { Location01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react-native';
import { InPersonEventButton } from '@molecules/InPersonEventButton';
import {
  useGetCommunity,
  useGetParentCommunityIds,
  useGetTicketCountForEvent,
} from '@sunrise-ui/api-client';
import { CommunityResult } from '@sunrise-ui/composites';
import {
  Card,
  Colors,
  ProfileImg,
  TextBold,
  TextReg,
  formatTimeRange,
  isAndroid,
  useDynamicStyles,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import { router } from 'expo-router';
import { DateTime } from 'luxon';
import React, { useEffect, useMemo, useState } from 'react';
import { StyleSheet, View, type ViewStyle } from 'react-native';
import Animated, {
  interpolateColor,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';

const MS_IN_24_HOURS = 24 * 60 * 60 * 1000;

type Props = {
  title?: string;
  locationId?: string;
  startDate: DateTime;
  endDate?: DateTime;
  eventId: string;
  communityId?: string;
  ticketingPlatformUrl?: string;
  style?: ViewStyle;
  hideParentCommunities?: boolean;
};

export default function InPersonEventCard({
  title,
  locationId,
  startDate,
  endDate,
  eventId,
  communityId,
  ticketingPlatformUrl,
  style,
  hideParentCommunities = false,
}: Props) {
  const [, forceUpdate] = useState({});

  const { location, isLoadingLocation } = useInPersonEventInformation(
    eventId,
    locationId,
  );
  const styles = useDynamicStyles(createStyles);

  // Check if event is currently ongoing
  const isEventOngoing = useMemo(() => {
    if (!startDate || !endDate) return false;
    const now = DateTime.now();
    return now >= startDate && now <= endDate;
  }, [startDate, endDate]);

  // Check if event starts within 24 hours
  const isUpcoming = useMemo(() => {
    if (!startDate) return false;
    const now = DateTime.now();
    if (startDate <= now) return false;
    const hoursUntilStart = startDate.diff(now, 'hours').hours;
    return hoursUntilStart <= 24;
  }, [startDate]);

  const shouldAnimate = isEventOngoing || isUpcoming;

  const animationProgress = useSharedValue(0);

  useEffect(() => {
    if (shouldAnimate) {
      animationProgress.value = withRepeat(
        withTiming(1, { duration: 1500 }),
        -1,
        true,
      );
    } else {
      animationProgress.value = withTiming(0, { duration: 300 });
    }
  }, [shouldAnimate, animationProgress]);

  const animatedBorderStyle = useAnimatedStyle(() => {
    if (!shouldAnimate)
      return {
        borderColor: 'transparent',
        borderWidth: 1.5,
      };

    const borderColor = interpolateColor(
      animationProgress.value,
      [0, 1],
      isEventOngoing
        ? [Colors.pink0, 'transparent']
        : isUpcoming
          ? [Colors.purple1, 'transparent']
          : ['transparent', 'transparent'],
    );

    return {
      borderColor,
      borderWidth: 1.5,
    };
  }, [shouldAnimate, isEventOngoing, isUpcoming, animationProgress]);

  const shadowColor = useMemo(() => {
    if (isEventOngoing) return Colors.pink0;
    if (isUpcoming) return Colors.purple1;
    return isAndroid ? Colors.opacity40 : Colors.opacity20;
  }, [isEventOngoing, isUpcoming]);

  // Set up timeout to update the component when event status changes
  useEffect(() => {
    if (!startDate || !endDate) return;

    let timeout: NodeJS.Timeout;
    const now = DateTime.now();
    const startTime = startDate;
    const endTime = endDate;

    // If event hasn't started yet, set timeout for when it starts
    if (now < startTime) {
      const msUntilStart = startTime.diff(now).milliseconds;
      if (msUntilStart > 0 && msUntilStart < MS_IN_24_HOURS) {
        timeout = setTimeout(() => {
          forceUpdate({});
        }, msUntilStart);
      }
    } else if (now <= endTime) {
      const msUntilEnd = endTime.diff(now).milliseconds;
      if (msUntilEnd > 0 && msUntilEnd < MS_IN_24_HOURS) {
        timeout = setTimeout(() => {
          forceUpdate({});
        }, msUntilEnd);
      }
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [startDate, endDate]);

  // Read ticket count from cache (populated by bulk query in EventCardsList)
  const { data: attendeeCount = 0 } = useGetTicketCountForEvent(
    ticketingPlatformUrl ? undefined : eventId,
  );

  // Get community to access its slug for navigation
  const { data: community } = useGetCommunity(communityId);

  // Get parent org communities for the event's community
  const { data: parentCommunityIds } = useGetParentCommunityIds({
    communityId,
    isActive: true,
  });

  const timeRange = formatTimeRange(
    startDate.toISO() ?? undefined,
    endDate?.toISO() ?? undefined,
  );

  const { name } = location ?? {};

  const handleCardPress = () => {
    if (community?.slug) {
      router.navigate(`/events/${community.slug}`);
    }
  };

  const isClickable = Boolean(community?.slug);

  return (
    <Animated.View style={[styles.animatedWrapper, style, animatedBorderStyle]}>
      <Card
        style={[styles.eventCard]}
        shadowColor={shadowColor}
        shadowOpacity={1}
        onPress={isClickable ? handleCardPress : undefined}
        activeOpacity={isClickable ? 0.8 : 1}
      >
        <View style={styles.topRow}>
          <View style={styles.leftColumn}>
            {timeRange && (
              <TextReg style={styles.timeRange}>{timeRange}</TextReg>
            )}
            {isLoadingLocation ? (
              <View style={styles.locationSkeleton} />
            ) : name ? (
              <View style={styles.locationContainer}>
                <HugeiconsIcon
                  icon={Location01Icon}
                  size={14}
                  color={Colors.text}
                  style={styles.locationIcon}
                />
                <TextReg style={styles.timeRange} numberOfLines={1}>
                  {name}
                </TextReg>
              </View>
            ) : null}
          </View>
          <View style={styles.buttonAndAttendeesContainer}>
            <InPersonEventButton eventId={eventId} />
            {!ticketingPlatformUrl && attendeeCount > 0 && (
              <TextReg style={styles.attendeeCount}>
                {attendeeCount} attending
              </TextReg>
            )}
          </View>
        </View>
        <View style={styles.eventInfo}>
          {!hideParentCommunities &&
            parentCommunityIds &&
            parentCommunityIds.length > 0 && (
              <View style={styles.communitiesList}>
                {parentCommunityIds.map((parentCommunityId) => (
                  <CommunityResult
                    key={parentCommunityId}
                    communityId={parentCommunityId}
                    size="small"
                    shouldNavigateOnClick={false}
                    isOneLineText={true}
                  />
                ))}
              </View>
            )}
          <View style={styles.titleRow}>
            {hideParentCommunities && community?.photos?.small && (
              <ProfileImg
                userId={communityId || ''}
                imgSize="small"
                styleSize={32}
                profile={{
                  first_name: community?.name,
                  last_name: '',
                  photos: community?.photos,
                }}
              />
            )}
            <TextBold
              style={[
                styles.title,
                hideParentCommunities && community?.photos?.small
                  ? styles.titleWithImage
                  : undefined,
              ]}
              numberOfLines={2}
            >
              {title}
            </TextBold>
          </View>
          {community?.headline && (
            <TextReg
              style={styles.description}
              numberOfLines={3}
              ellipsizeMode="tail"
            >
              {community.headline}
            </TextReg>
          )}
        </View>
      </Card>
    </Animated.View>
  );
}

const createStyles = ({ isMobile }: WithResponsive) =>
  StyleSheet.create({
    animatedWrapper: {
      overflow: 'visible',
      borderRadius: isMobile ? 16 : 30,
    },
    topRow: {
      display: 'flex',
      flexDirection: 'row',
      gap: isMobile ? 12 : 20,
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      width: '100%',
    },
    leftColumn: {
      flex: 1,
      flexDirection: 'column',
      minWidth: 0,
      overflow: 'hidden',
    },
    locationContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    locationIcon: {
      opacity: 0.8,
    },
    timeRange: {
      fontSize: isMobile ? 12 : 14,
      color: Colors.text,
      opacity: 0.8,
    },
    eventCard: {
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      minWidth: 200,
      borderRadius: 12,
    },
    eventInfo: {
      gap: isMobile ? 8 : 12,
      width: '100%',
    },
    title: {
      fontSize: isMobile ? 20 : 24,
    },
    description: {
      fontSize: isMobile ? 14 : 16,
      flexShrink: 1,
    },
    buttonAndAttendeesContainer: {
      alignItems: 'center',
      gap: isMobile ? 4 : 8,
    },
    attendeeCount: {
      fontSize: isMobile ? 12 : 14,
      color: Colors.text,
      opacity: 0.8,
    },
    locationSkeleton: {
      width: '100%',
      maxWidth: 200,
      height: isMobile ? 12 : 14,
      marginTop: 2.75,
      marginBottom: 2.75,
      borderRadius: 16,
      backgroundColor: Colors.opacity20,
    },
    communitiesList: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
      width: '100%',
    },
    titleRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      width: '100%',
    },
    titleWithImage: {
      flex: 1,
    },
  });
